import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, LayoutDashboard, LogOut, PlayCircle, History, User, CreditCard, Lock } from 'lucide-react';
import { Button } from './Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import { BrandMark } from './BrandMark';

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  
  const dashboardDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const navItems = [
    { label: t('header.home'), href: '/', isRoute: true },
    { label: t('about.title'), href: '/about', isRoute: true },
    { label: t('header.pricing'), href: '/#pricing', isRoute: false },
    { label: t('contact.title'), href: '/contact', isRoute: true },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        dashboardDropdownRef.current && !dashboardDropdownRef.current.contains(target) &&
        userDropdownRef.current && !userDropdownRef.current.contains(target)
      ) {
        setOpenDropdown(null);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    await logout();
  };

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    
    if (isRoute) {
      navigate(href);
    } else if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate(href);
      } else {
        const hash = href.replace('/', '');
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const userEmail = user?.email;
  const isSubscribed = user?.subscription?.status === 'active';

  return (
    <header className="sticky top-0 z-50 border-b border-primary/10 bg-white/90 shadow-sm backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size="sm" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href, item.isRoute)}
                className="rounded-xl px-3 py-2 font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher variant="compact" />

            {isAuthenticated ? (
              <>
                <Button 
                  variant="primary" 
                  size="small"
                  onClick={() => navigate('/exam-selection')}
                  icon={<PlayCircle className="w-4 h-4" />}
                >
                  {t('header.startExam')}
                </Button>

                <div className="relative" ref={dashboardDropdownRef}>
                  <button
                    onClick={() => toggleDropdown('dashboard')}
                    className={cn(
                  'flex items-center gap-2 rounded-xl px-3 py-2 font-medium transition-colors',
                      openDropdown === 'dashboard' 
                        ? 'text-primary bg-primary/5' 
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    )}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t('header.dashboard')}
                    <ChevronDown className={cn('w-4 h-4 transition-transform', openDropdown === 'dashboard' && 'rotate-180')} />
                  </button>

                  {openDropdown === 'dashboard' && (
                    <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-2xl border border-primary/10 bg-white py-2 shadow-soft animate-in fade-in slide-in-from-top-2 duration-150">
                      <button
                        onClick={() => { setOpenDropdown(null); navigate('/dashboard'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {t('profile.tabs.overview')}
                      </button>
                      <button
                        onClick={() => { setOpenDropdown(null); navigate('/exam-history'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                      >
                        <History className="w-4 h-4" />
                        <span className="flex flex-col">
                          <span>{t('header.history')}</span>
                          {!isSubscribed && (
                            <span className="flex items-center gap-1 text-[11px] font-normal text-amber-600">
                              <Lock className="w-3 h-3" />
                              {t('header.subscriptionRequired')}
                            </span>
                          )}
                        </span>
                      </button>
                      <button
                        onClick={() => { setOpenDropdown(null); navigate('/dashboard/subscription'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                      >
                        <CreditCard className="w-4 h-4" />
                        {t('profile.subscription')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="relative" ref={userDropdownRef}>
                  <button
                    onClick={() => toggleDropdown('user')}
                    className={cn(
                      'flex items-center gap-2 rounded-xl px-2 py-2 transition-colors',
                      openDropdown === 'user' 
                        ? 'bg-primary/5' 
                        : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm">
                      {userEmail?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <ChevronDown className={cn('w-4 h-4 text-gray-500 transition-transform', openDropdown === 'user' && 'rotate-180')} />
                  </button>

                  {openDropdown === 'user' && (
                    <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-2xl border border-primary/10 bg-white py-2 shadow-soft animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="border-b border-primary/10 px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{t('common.welcome')}!</p>
                        <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                      </div>
                      
                      <button
                        onClick={() => { setOpenDropdown(null); navigate('/profile'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                      >
                        <User className="w-4 h-4" />
                        {t('header.profile')}
                      </button>
                      
                      <div className="mt-2 border-t border-primary/10 pt-2">
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-red-600 transition-colors hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('header.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="px-4 py-2 text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  {t('header.login')}
                </Link>
                <Button 
                  variant="primary" 
                  size="small"
                  onClick={() => navigate('/try-free')}
                >
                  {t('header.tryFree')}
                </Button>
              </>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="rounded-lg p-2 text-slate-700 transition-colors hover:bg-primary/5 lg:hidden"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-primary/10 bg-white shadow-lg animate-in slide-in-from-top-2 duration-200 lg:hidden">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href, item.isRoute)}
                className="block w-full rounded-lg px-4 py-3 text-left font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
              >
                {item.label}
              </button>
            ))}
            
            <div className="border-t border-primary/10 px-4 py-2">
              <LanguageSwitcher variant="full" />
            </div>

            <div className="space-y-2 border-t border-primary/10 pt-4">
              {isAuthenticated ? (
                <>
                  <Button 
                    variant="primary" 
                    className="w-full"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/exam-selection');
                    }}
                    icon={<PlayCircle className="w-4 h-4" />}
                  >
                    {t('header.startExam')}
                  </Button>
                  
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/dashboard');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t('header.dashboard')}
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/exam-history');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                  >
                    <History className="w-4 h-4" />
                    <span className="flex flex-col text-left">
                      <span>{t('header.history')}</span>
                      {!isSubscribed && (
                        <span className="flex items-center gap-1 text-[11px] font-normal text-amber-600">
                          <Lock className="w-3 h-3" />
                          {t('header.subscriptionRequired')}
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                  >
                    <User className="w-4 h-4" />
                    {t('header.profile')}
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/dashboard/subscription');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 font-medium text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t('profile.subscription')}
                  </button>
                  
                  <div className="border-t border-primary/10 pt-3">
                    <div className="px-4 py-2 text-xs text-gray-500 truncate">
                      {userEmail}
                    </div>
                    <Button 
                      variant="tertiary" 
                      className="w-full text-red-600 hover:bg-red-50"
                      onClick={handleLogout}
                      icon={<LogOut className="w-4 h-4" />}
                    >
                      {t('header.logout')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="block py-3 px-4 text-center text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('header.login')}
                  </Link>
                  <Button 
                    variant="primary" 
                    className="w-full"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/try-free');
                    }}
                  >
                    {t('header.tryFree')}
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
